import { FC, useState, useEffect, useCallback } from 'react'
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
  Spinner,
  Typography,
} from '@material-tailwind/react'
import { PiQuestionDuotone } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import Editor from 'react-simple-code-editor'
import Ajv from 'ajv'
import { highlight, languages } from 'prismjs/components/prism-core'
import { theme } from '../tailwind.config'
import { useAgamiIndoorConfig } from '../hooks/useAgamiIndoorConfig'

// JSON validator
import { AGAMI_INDOOR_CONFIG_SCHEMA } from '../utils/constants/ajv'

// prismjs syntax highlighter
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism.css'

const ajv = new Ajv()
const validate = ajv.compile(AGAMI_INDOOR_CONFIG_SCHEMA)

const VALID_EXAMPLE = `[
  {
    "id": "unique-spot-id",
    "url": "sound.mp3",
    "maxVolume": 2.0,
    "minVolume": 10.0,
    "position": {
      "x": 4.5,
      "y": 3.0
    }
  }
]`

const AgamiIndoorConfigEditor: FC = () => {
  const { t } = useTranslation()
  const { config, isDownloading, save, isSaving } = useAgamiIndoorConfig()
  const [editorConfig, setEditorConfig] = useState('')
  const [helpOpen, setHelpOpen] = useState(false)
  const [isValidJson, setIsValidJson] = useState(true)

  useEffect(() => {
    if (!isDownloading && !!config) {
      setEditorConfig(String(config))
    }
  }, [isDownloading, config])

  const [isChanged, setIsChanged] = useState(false)

  useEffect(() => {
    if (!config) {
      setIsChanged(editorConfig !== '')
    } else {
      setIsChanged(editorConfig !== String(config))
    }
  }, [editorConfig, config])

  const saveConfig = useCallback(() => {
    try {
      save(editorConfig)
    } catch {
      // should be valid json always
    }
  }, [save, editorConfig])

  useEffect(() => {
    if (!editorConfig) return
    try {
      const parsed = JSON.parse(editorConfig)
      const valid = validate(parsed)
      setIsValidJson(valid)
    } catch {
      // JSON validation error
      setIsValidJson(false)
    }
  }, [editorConfig])

  return (
    <div>
      <Dialog size="lg" open={helpOpen} handler={() => setHelpOpen((prev) => !prev)}>
        <DialogHeader>{t('indoorConfig.help.title')}</DialogHeader>
        <DialogBody>
          <Typography className="mb-4">{t('indoorConfig.help.description')}</Typography>

          <div className="w-full relative">
            <div className="absolute top-2 right-2">
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setEditorConfig(VALID_EXAMPLE)
                  setHelpOpen(false)
                }}
              >
                {t('indoorConfig.help.tryExample')}
              </Button>
            </div>
            <pre className="bg-gray-100 text-xs p-4 rounded-md overflow-x-auto mb-4">
              {`[
                {
                  "id": "unique-spot-id", // ${t('indoorConfig.help.example.id')}
                  "url": "sound.mp3",     // ${t('indoorConfig.help.example.url')}
                  "maxVolume": 2.0,       // ${t('indoorConfig.help.example.maxVolume')}
                  "minVolume": 10.0,      // ${t('indoorConfig.help.example.minVolume')}
                  "position": {
                    "x": 4.5,             // ${t('indoorConfig.help.example.positionX')}
                    "y": 3.0              // ${t('indoorConfig.help.example.positionY')}
                  }
                },
                { ... }
              ]`}
            </pre>
          </div>

          <div className="mb-2">
            <Typography>
              <strong>{t('indoorConfig.help.tips.title')}</strong>
            </Typography>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 font-sans">
              <li>{t('indoorConfig.help.tips.maxVolume')}</li>
              <li>{t('indoorConfig.help.tips.coordinates')}</li>
              <li>{t('indoorConfig.help.tips.uploadFiles')}</li>
            </ul>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outlined" onClick={() => setHelpOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="flex gap-2 mb-2 items-center">
        <Typography variant="h3">{t('indoorConfig.jsonConfig')}</Typography>

        <IconButton size="sm" variant="text">
          <PiQuestionDuotone onClick={() => setHelpOpen(true)} className="h-5 md:w-5" />
        </IconButton>
        {isChanged && !isDownloading && (
          <Button size="sm" disabled={isSaving || !isValidJson || !editorConfig} onClick={saveConfig}>
            {isSaving ? <Spinner className="h-4 w-4" /> : t('common.save')}
          </Button>
        )}
      </div>

      {isDownloading ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <>
          <Editor
            value={editorConfig}
            onValueChange={(json) => setEditorConfig(json)}
            highlight={(code) => highlight(code, languages.json)}
            padding={10}
            style={{
              fontFamily: 'monospace',
              fontSize: 12,
              backgroundColor: isValidJson
                ? theme.extend.colors['white-yellow'][500]
                : theme.extend.colors['accent-red'][50],
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: isValidJson
                ? theme.extend.colors['base-gray'][500]
                : theme.extend.colors['accent-red'][800],
              borderRadius: '5px',
            }}
          />
          {!isValidJson && (
            <Typography className="mt-1 text-accent-red-800 text-sm italic">
              {t('indoorConfig.invalidJson')}
            </Typography>
          )}
        </>
      )}
    </div>
  )
}

export default AgamiIndoorConfigEditor
